import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import {
  LocationOn as LocationIcon
} from '@mui/icons-material';
import type { Person, Location } from '../../../../types';

interface LocationsPanelProps {
  person: Person;
  showMetadata?: boolean;
  metadataFormat?: 'short' | 'long';
}

/**
 * LocationsPanel - Displays location information
 *
 * Shows:
 * - Birth place (full address)
 * - Death place (full address)
 * - Current location
 */
export const LocationsPanel: React.FC<LocationsPanelProps> = ({
  person,
  showMetadata = false,
  metadataFormat = 'short'
}) => {
  const formatLocation = (location?: Location): string => {
    if (!location) return '';
    const parts = [location.city, location.state, location.country].filter(Boolean);
    return parts.join(', ');
  };

  const birthPlace = formatLocation(person.birthPlace);
  const currentLocation = formatLocation(person.currentLocation);
  const deathPlace = formatLocation(person.deathPlace);
  const hasLocations = birthPlace || currentLocation || deathPlace;

  if (!hasLocations) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
        No location information available
      </Typography>
    );
  }

  return (
    <Box>
      <List dense>
        {/* Birth Place */}
        {birthPlace && (
          <ListItem>
            <ListItemIcon>
              <LocationIcon />
            </ListItemIcon>
            <ListItemText
              primary="Birth Place"
              secondary={birthPlace}
            />
          </ListItem>
        )}

        {/* Current Location */}
        {currentLocation && (
          <ListItem>
            <ListItemIcon>
              <LocationIcon />
            </ListItemIcon>
            <ListItemText
              primary="Current Location"
              secondary={currentLocation}
            />
          </ListItem>
        )}

        {/* Death Place */}
        {deathPlace && (
          <ListItem>
            <ListItemIcon>
              <LocationIcon />
            </ListItemIcon>
            <ListItemText
              primary="Death Place"
              secondary={deathPlace}
            />
          </ListItem>
        )}
      </List>
    </Box>
  );
};
